import React from 'react';
import {
    List,
    ListSubheader,
    Divider,
    IconButton,
    Typography,
    Container,
    Box,
    TextField,
    InputAdornment,
    Checkbox,
    Button,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Close, Search } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { isValidCharWithoutSpace } from '../../lib/site-helper';
import { Actions } from '../../actions';
import { useDeviceTrendData } from '../../data/DataProviders/DeviceTrendDataProvider';
import { useDispatch, useSelector } from 'react-redux';
import { TrendChannelSelectors } from '../../selectors/trend-channel-selectors';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { Selectors } from '../../selectors';
import { isTrendlineEnabled } from '../../state/dashboard-state';

const useStyles = makeStyles(() => ({
    root: {
        margin: '5px',
    },
    TypoMessage: {
        fontFamily: 'Open Sans',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '12px',
        lineHeight: '16px',
        display: 'flex',
        alignItems: 'center',
    },
    Container: {
        margin: '10px',
        padding: '10px',
        height: '100%',
        overflow: 'hidden',
    },
    Paper: {
        height: '83%',
        overflow: 'auto',
        width: '100%',
        '&::-webkit-scrollbar': {
            width: '10px',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 5px grey',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'grey',
            borderRadius: '10px',
        },
    },
}));

const SearchTextField = withStyles({
    root: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '28px',
        },
    },
})(TextField);

export const CustomizeChannels: React.FC<{
    toggleDrawer: Function;
    channels: {
        maxTrendChannelToBeAllowedFromUser: number;
        trendChannels: Array<{ id: string; channelName: string; wasPriority: string | null }>;
    };
    deviceId: string;
    save: Function;
}> = (props) => {
    const [maxChannelsAllowed, setMaxChannelsAllowed] = React.useState<number>(
        props.channels.maxTrendChannelToBeAllowedFromUser
    );
    const classes = useStyles();
    const [query, setQuery] = React.useState('');
    const dispatch = useDispatch();
    const trendChannels = useSelector(TrendChannelSelectors.selectedChannels);
    const trendData = useDeviceTrendData();
    const trendLinesEnabled = useSelector(Selectors.Dashboard.trendLinesEnabled);

    const [allChannels, setAllChannels] = React.useState<Array<{ id: string; channelName: string }>>(
        props.channels.trendChannels
    );
    const [mainChannels, setMainChannels] = React.useState<Array<{ id: string; channelName: string }>>(
        props.channels.trendChannels
    );
    const [unmodifiedMainChannels, setunmodifiedMainChannels] = React.useState<
        Array<{ id: string; channelName: string }>
    >(props.channels.trendChannels);
    const [displayAllChannels, setAllDisplayChannels] = React.useState<Array<{ id: string; channelName: string }>>([]);
    const [displayMainChannels, setMainDisplayChannels] = React.useState<Array<{ id: string; channelName: string }>>(
        []
    );
    const [isDirty, setIsDirty] = React.useState(false);

    const { t } = useLanguageLocale();

    React.useEffect(() => {
        const tempmod = JSON.parse(JSON.stringify(mainChannels));
        const tempunmod = JSON.parse(JSON.stringify(unmodifiedMainChannels));
        const unmodData = tempunmod.sort((a: any, b: any) => (a.channelName > b.channelName ? 1 : -1));
        const modData = tempmod.sort((a: any, b: any) => (a.channelName > b.channelName ? 1 : -1));
        const isDataModified = JSON.stringify(unmodData) !== JSON.stringify(modData);

        setIsDirty(isDataModified);
    }, [unmodifiedMainChannels, mainChannels]);

    React.useEffect(() => {
        setMaxChannelsAllowed(props.channels.maxTrendChannelToBeAllowedFromUser);
        // let selectedChannels: Array<{ id: string; name: string }> = [];

        const tempMainChannels: Array<{ id: string; channelName: string }> = [];
        trendChannels.map((item) => {
            const foundRecord = props.channels.trendChannels.find((x) => x.id === item.id);
            if (foundRecord) {
                if (isTrendlineEnabled(trendLinesEnabled, Number(foundRecord.id), props.deviceId))
                    tempMainChannels.push(foundRecord);
            }
            return tempMainChannels;
        });
        setMainChannels(trendChannels);
        setunmodifiedMainChannels(JSON.parse(JSON.stringify(trendChannels)));
        setMainDisplayChannels(trendChannels);

        const tempAllChannels = props.channels.trendChannels
            .filter((i) => !trendChannels.filter((y) => y.id.toString() === i.id).length)
            .map((item) => ({ id: item.id, channelName: item.channelName }));
        setAllChannels(tempAllChannels);

        setAllDisplayChannels(tempAllChannels);
    }, [props.channels, trendChannels, trendLinesEnabled, props.deviceId]);

    const handleToggle = (value: any, from: string) => (): void => {
        const tempMainChannels = mainChannels.map((a) => ({ ...a }));
        const tempAllChannels = allChannels.map((a) => ({ ...a }));

        if (from === 'All') {
            const foundRecord = props.channels.trendChannels.find((x) => x.id === value);

            if (foundRecord) {
                tempMainChannels.unshift(foundRecord);

                setMainChannels(tempMainChannels);
                setMainDisplayChannels(tempMainChannels);

                const channels = tempAllChannels.filter((x) => x.id !== value);
                setAllChannels(channels);
                setAllDisplayChannels(channels);
            }
        } else {
            const foundRecord = props.channels.trendChannels.find((x) => x.id === value.toString());

            if (foundRecord) {
                tempAllChannels.unshift(foundRecord);

                setAllChannels(tempAllChannels);
                setAllDisplayChannels(tempAllChannels);
                const channels = tempMainChannels.filter((x) => x.id !== value);
                setMainChannels(channels);
                setMainDisplayChannels(channels);
            }
        }
    };
    const searchResults = React.useCallback(
        (searchString: string, data: Array<{ id: string; channelName: string }>): any[] => {
            const q = searchString.toLowerCase().trim();
            return data.filter((item: any): boolean => {
                if (
                    item.channelName
                        .toLowerCase()
                        .trim()
                        .includes(q)
                ) {
                    return true;
                }
                return false;
            });
        },
        [allChannels]
    );

    React.useEffect(() => {
        if (query !== '') {
            setAllDisplayChannels(searchResults(query, allChannels));
            setMainDisplayChannels(searchResults(query, mainChannels));
        } else {
            setAllDisplayChannels(allChannels);
            setMainDisplayChannels(mainChannels);
        }
    }, [query, searchResults]);

    const onSave = (): void => {
        const tempMainChannels: number[] = [];
        // console.log('tempMainChannels', tempMainChannels);
        mainChannels.map((item) => {
            tempMainChannels.push(Number(item.id));
            return tempMainChannels;
        });

        dispatch(Actions.TrendChannels.trendChannelsForGraph(tempMainChannels));
        trendData.fetchTrendData(tempMainChannels);
        props.save();
    };

    const onChangeChannelName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            setQuery(e.target.value);
        }
    };

    const resetSearch = (): void => {
        setQuery('');
        setAllDisplayChannels(allChannels);
        setMainDisplayChannels(mainChannels);
    };

    return (
        <>
            <List
                disablePadding
                subheader={
                    <ListSubheader
                        color="primary"
                        component="div"
                        style={{
                            fontWeight: 600,
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        Customize channels
                        <IconButton data-testid="close-icon" onClick={props.toggleDrawer(false)} size="large">
                            <Close fontSize="small" />
                        </IconButton>
                    </ListSubheader>
                }
            ></List>
            <Divider />
            <Container className={classes.Container}>
                <Typography variant="body2" className={classes.TypoMessage}>
                    Select up to {maxChannelsAllowed} &nbsp;channels to add to your trend viewer. Although the device
                    may have more than {maxChannelsAllowed} &nbsp;channels, it will only be possible to view the
                    selected ones.
                </Typography>

                <Box display="flex" justifyContent="flex-start" style={{ marginTop: '20px' }}>
                    <SearchTextField
                        inputProps={{ 'data-testid': 'search', maxLength: 100 }}
                        style={{ width: 300, borderRadius: 8 }}
                        value={query}
                        size="small"
                        placeholder={'Search Channel'}
                        onChange={(e): void => onChangeChannelName(e)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="end">
                                    <Search data-testid="search-icon" fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {query !== '' && (
                                        <IconButton onClick={resetSearch}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                    />
                </Box>

                <div className={classes.Paper}>
                    <Box style={{ marginTop: '20px' }}>
                        <Typography variant="overline">
                            SELECTED CHANNELS ({mainChannels.length}/{maxChannelsAllowed})
                        </Typography>
                    </Box>
                    <List className={classes.root}>
                        {displayMainChannels.length === 0 ? (
                            <Typography variant="caption"> No Channels </Typography>
                        ) : (
                            displayMainChannels.map((channel) => {
                                const labelId = `checkbox-list-label-${channel.id}`;

                                return (
                                    <ListItem
                                        key={channel.id}
                                        role={undefined}
                                        dense
                                        button
                                        onClick={handleToggle(channel.id, 'Main')}
                                        data-testid={channel.id}
                                    >
                                        <ListItemIcon style={{ minWidth: '30px' }}>
                                            <Checkbox
                                                edge="start"
                                                checked={true}
                                                tabIndex={-1}
                                                disableRipple
                                                inputProps={{ 'aria-labelledby': labelId }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText id={channel.id} primary={channel.channelName} />
                                    </ListItem>
                                );
                            })
                        )}
                    </List>

                    <Box style={{ marginTop: '20px' }}>
                        <Typography variant="overline">UNSELECTED CHANNELS</Typography>
                    </Box>
                    <List className={classes.root}>
                        {displayAllChannels.length === 0 ? (
                            <Typography variant="caption"> No Channels </Typography>
                        ) : (
                            displayAllChannels.map((channel) => {
                                const labelId = `checkbox-list-label-${channel.id}`;

                                return (
                                    <ListItem
                                        key={channel.id}
                                        role={undefined}
                                        dense
                                        button
                                        onClick={handleToggle(channel.id, 'All')}
                                        disabled={mainChannels.length === maxChannelsAllowed}
                                        data-testid={channel.id}
                                    >
                                        <ListItemIcon style={{ minWidth: '30px' }}>
                                            <Checkbox
                                                edge="start"
                                                checked={false}
                                                tabIndex={-1}
                                                disableRipple
                                                inputProps={{ 'aria-labelledby': labelId }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText id={channel.id} primary={channel.channelName} />
                                    </ListItem>
                                );
                            })
                        )}
                    </List>
                </div>
            </Container>

            <Divider />
            <Box>
                <Button
                    style={{ margin: 12 }}
                    variant="outlined"
                    color="primary"
                    data-testid="cancel-button"
                    onClick={props.toggleDrawer(false)}
                >
                    {t('ACTIONS.CANCEL')}
                </Button>
                <Button
                    style={{ float: 'right', margin: 12 }}
                    variant="contained"
                    color="primary"
                    data-testid="save-button"
                    onClick={onSave}
                    disabled={mainChannels.length === 0 || !isDirty}
                >
                    Save
                </Button>
            </Box>
        </>
    );
};
